import { Suspense } from 'react';
import { lazyPage } from '@/utils/lazyPage';
import { isAppChannel } from '@/lib/channel';
import { Routes, Route, Navigate } from 'react-router-dom';
// Eager-loaded: Home (Landing-Page) + Legal-Pages (kleine Inhalte,
// rechtlich relevant, sollten ohne Lazy-Boundary funktionieren)
import { Home } from './pages/public/Home';
import { NotFound } from './pages/public/NotFound';
import { RequireAuth } from './features/auth/RequireAuth';
import type { StaffRole } from './features/auth/useAuth';
import { AuthProvider } from './features/auth/AuthProvider';
import { SaveStatusProvider } from './features/save/SaveStatusProvider';
import { NativePushNavigation } from './features/push/NativePushNavigation';
import { PublicPageTemplate } from './components/layout';
import { Tricolore } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Loader2 } from 'lucide-react';
import { ButtonLink } from '@/components/ui';

// Public-Pages lazy: jede Seite wird erst beim Navigieren geladen.
// Reduziert den Initial-Bundle deutlich (550 kB → ~200 kB Ziel).
const Impressum = lazyPage(() => import('./pages/public/Impressum').then((m) => ({ default: m.Impressum })));
const Datenschutz = lazyPage(() => import('./pages/public/Datenschutz').then((m) => ({ default: m.Datenschutz })));
const AGB = lazyPage(() => import('./pages/public/AGB').then((m) => ({ default: m.AGB })));
const UeberUns = lazyPage(() => import('./pages/public/UeberUns').then((m) => ({ default: m.UeberUns })));
const Kontakt = lazyPage(() => import('./pages/public/Kontakt').then((m) => ({ default: m.Kontakt })));
const Speisekarte = lazyPage(() => import('./pages/public/Speisekarte').then((m) => ({ default: m.Speisekarte })));
const Reservieren = lazyPage(() => import('./pages/public/Reservieren').then((m) => ({ default: m.Reservieren })));
const ReservationConfirmed = lazyPage(() => import('./pages/public/ReservationConfirmed').then((m) => ({ default: m.ReservationConfirmed })));
const ReservationCancel = lazyPage(() => import('./pages/public/ReservationCancel').then((m) => ({ default: m.ReservationCancel })));
const ReservationManage = lazyPage(() => import('./pages/public/ReservationManage').then((m) => ({ default: m.ReservationManage })));
const WaitlistClaim = lazyPage(() => import('./pages/public/WaitlistClaim').then((m) => ({ default: m.WaitlistClaim })));
const Events = lazyPage(() => import('./pages/public/Events').then((m) => ({ default: m.Events })));
const ApeAnfrageErhalten = lazyPage(() => import('./pages/public/ApeAnfrageErhalten').then((m) => ({ default: m.ApeAnfrageErhalten })));
const ApeQuoteAccept = lazyPage(() => import('./pages/public/ApeQuoteAccept').then((m) => ({ default: m.ApeQuoteAccept })));
const ApeBuchungBestaetigt = lazyPage(() => import('./pages/public/ApeBuchungBestaetigt').then((m) => ({ default: m.ApeBuchungBestaetigt })));
const ApeStorno = lazyPage(() => import('./pages/public/ApeStorno').then((m) => ({ default: m.ApeStorno })));
const EventDetail = lazyPage(() => import('./pages/public/EventDetail').then((m) => ({ default: m.EventDetail })));
const Galerie = lazyPage(() => import('./pages/public/Galerie').then((m) => ({ default: m.Galerie })));
const LaPutia = lazyPage(() => import('./pages/public/LaPutia').then((m) => ({ default: m.LaPutia })));
const Newsletter = lazyPage(() => import('./pages/public/Newsletter').then((m) => ({ default: m.Newsletter })));
const NewsletterConfirmed = lazyPage(() => import('./pages/public/NewsletterConfirmed').then((m) => ({ default: m.NewsletterConfirmed })));
const NewsletterUnsubscribed = lazyPage(() => import('./pages/public/NewsletterUnsubscribed').then((m) => ({ default: m.NewsletterUnsubscribed })));
const DesignSystemShowcase = lazyPage(() => import('./pages/dev/DesignSystem').then((m) => ({ default: m.DesignSystemShowcase })));
const SymbolGallery = lazyPage(() => import('./pages/dev/SymbolGallery').then((m) => ({ default: m.SymbolGallery })));

// Admin-Pages werden lazy geladen — sind nur für Mitarbeiter relevant
// und reduzieren das Public-Bundle deutlich.
const AdminLogin = lazyPage(() => import('./pages/admin/AdminLogin').then((m) => ({ default: m.AdminLogin })));
const PinLogin = lazyPage(() => import('./pages/admin/PinLogin').then((m) => ({ default: m.PinLogin })));
const KontorEintritt = lazyPage(() => import('./pages/admin/KontorEintritt'));
const GeraetEntsperren = lazyPage(() => import('./pages/admin/GeraetEntsperren').then((m) => ({ default: m.GeraetEntsperren })));
const MeinKonto = lazyPage(() => import('./pages/admin/MeinKonto').then((m) => ({ default: m.MeinKonto })));
const AdminMfaChallenge = lazyPage(() => import('./pages/admin/AdminMfaChallenge').then((m) => ({ default: m.AdminMfaChallenge })));
const AdminOnboarding = lazyPage(() => import('./pages/admin/AdminOnboarding').then((m) => ({ default: m.AdminOnboarding })));
const AdminPasswordSet = lazyPage(() => import('./pages/admin/AdminPasswordSet').then((m) => ({ default: m.AdminPasswordSet })));
const AdminDashboard = lazyPage(() => import('./pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
const AdminEventsList = lazyPage(() => import('./pages/admin/AdminEventsList').then((m) => ({ default: m.AdminEventsList })));
const AdminEventDetail = lazyPage(() => import('./pages/admin/AdminEventDetail').then((m) => ({ default: m.AdminEventDetail })));
const AdminEventNew = lazyPage(() => import('./pages/admin/AdminEventNew').then((m) => ({ default: m.AdminEventNew })));
const AdminEventCalendar = lazyPage(() => import('./pages/admin/AdminEventCalendar').then((m) => ({ default: m.AdminEventCalendar })));
const ApeVerkaufListe = lazyPage(() => import('./pages/admin/ApeVerkaufListe').then((m) => ({ default: m.ApeVerkaufListe })));
const ApeVerkaufStationA = lazyPage(() => import('./pages/admin/ApeVerkaufStationA').then((m) => ({ default: m.ApeVerkaufStationA })));
const ApeVerkaufStationB = lazyPage(() => import('./pages/admin/ApeVerkaufStationB').then((m) => ({ default: m.ApeVerkaufStationB })));
const ApeVerkaufBericht = lazyPage(() => import('./pages/admin/ApeVerkaufBericht').then((m) => ({ default: m.ApeVerkaufBericht })));
const Tafel = lazyPage(() => import('./pages/public/Tafel').then((m) => ({ default: m.Tafel })));
const TafelVerkauf = lazyPage(() => import('./pages/public/TafelVerkauf').then((m) => ({ default: m.TafelVerkauf })));
const ApeKatalog = lazyPage(() => import('./pages/admin/ApeKatalog').then((m) => ({ default: m.ApeKatalog })));
const ApeRechner = lazyPage(() => import('./pages/admin/ApeRechner').then((m) => ({ default: m.ApeRechner })));
const ApeVerwaltung = lazyPage(() => import('./pages/admin/ApeVerwaltung').then((m) => ({ default: m.ApeVerwaltung })));
const AdminInsights = lazyPage(() => import('./pages/admin/AdminInsights').then((m) => ({ default: m.AdminInsights })));
const AdminChatbot = lazyPage(() => import('./pages/admin/AdminChatbot').then((m) => ({ default: m.AdminChatbot })));
const AdminSeo = lazyPage(() => import('./pages/admin/AdminSeo').then((m) => ({ default: m.AdminSeo })));
const AdminMenu = lazyPage(() => import('./pages/admin/AdminMenu').then((m) => ({ default: m.AdminMenu })));
const AdminMargins = lazyPage(() => import('./pages/admin/AdminMargins').then((m) => ({ default: m.AdminMargins })));
const AdminShop = lazyPage(() => import('./pages/admin/AdminShop').then((m) => ({ default: m.AdminShop })));
const AdminReservations = lazyPage(() => import('./pages/admin/AdminReservations').then((m) => ({ default: m.AdminReservations })));
const AdminReservationCalendar = lazyPage(() => import('./pages/admin/AdminReservationCalendar').then((m) => ({ default: m.AdminReservationCalendar })));
const AdminMail = lazyPage(() => import('./pages/admin/AdminMail').then((m) => ({ default: m.AdminMail })));
const AdminNewsletterCampaigns = lazyPage(() => import('./pages/admin/AdminNewsletterCampaigns').then((m) => ({ default: m.AdminNewsletterCampaigns })));
const AdminNewsletterSubscribers = lazyPage(() => import('./pages/admin/AdminNewsletterSubscribers').then((m) => ({ default: m.AdminNewsletterSubscribers })));
const AdminDiscountCodes = lazyPage(() => import('./pages/admin/AdminDiscountCodes').then((m) => ({ default: m.AdminDiscountCodes })));
const AdminInventory = lazyPage(() => import('./pages/admin/AdminInventory').then((m) => ({ default: m.AdminInventory })));
const AdminShifts = lazyPage(() => import('./pages/admin/AdminShifts').then((m) => ({ default: m.AdminShifts })));
const AdminTischuebersicht = lazyPage(() => import('./pages/admin/AdminTischuebersicht').then((m) => ({ default: m.AdminTischuebersicht })));
const AdminWaitlist = lazyPage(() => import('./pages/admin/AdminWaitlist').then((m) => ({ default: m.AdminWaitlist })));
const AdminQrCodes = lazyPage(() => import('./pages/admin/AdminQrCodes').then((m) => ({ default: m.AdminQrCodes })));
const AdminSettings = lazyPage(() => import('./pages/admin/AdminSettings').then((m) => ({ default: m.AdminSettings })));
const AdminCronStatus = lazyPage(() => import('./pages/admin/AdminCronStatus').then((m) => ({ default: m.AdminCronStatus })));
const AdminCronProbe = lazyPage(() => import('./pages/admin/AdminCronProbe').then((m) => ({ default: m.AdminCronProbe })));
const AdminPersonal = lazyPage(() => import('./pages/admin/AdminPersonal').then((m) => ({ default: m.AdminPersonal })));
const AdminContactMessages = lazyPage(() => import('./pages/admin/AdminContactMessages').then((m) => ({ default: m.AdminContactMessages })));
const AdminProtokoll = lazyPage(() => import('./pages/admin/AdminProtokoll').then((m) => ({ default: m.AdminProtokoll })));
const AdminService = lazyPage(() => import('./pages/admin/AdminService').then((m) => ({ default: m.AdminService })));

/**
 * Fallback-Loader für lazy-loaded Admin-Pages.
 * Zeigt Brand-Spinner zentriert bis Chunk geladen ist.
 */
function AdminLoader() {
  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="size-8 mx-auto animate-spin text-gold" />
        <p className="font-sans text-xs text-ink-mute mt-3 tracking-[0.22em] uppercase">
          Lade Backoffice…
        </p>
      </div>
    </div>
  );
}

/**
 * Minimal-Loader für lazy-loaded Public-Pages.
 * Bewusst klein gehalten — meiste Chunks laden in < 200 ms.
 */
function PublicLoader() {
  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <Loader2 className="size-6 animate-spin text-gold" />
    </div>
  );
}

/**
 * Wrapper für Public-Route + Suspense (DRY für alle lazy Public-Pages).
 *
 * Kanal-Guard: die native Shell (App-Kanal) zeigt NUR das Backoffice — im
 * App-Kanal leiten Public-Routen hart auf /admin (RequireAuth übernimmt dort
 * ggf. den Login-Redirect). Web-Verhalten bleibt byte-identisch. Neue
 * Public-Routen IMMER hiermit wrappen — auch eager geladene (CLAUDE.md
 * „Kanal-Architektur").
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  if (isAppChannel()) return <Navigate to="/admin" replace />;
  return <Suspense fallback={<PublicLoader />}>{children}</Suspense>;
}

/**
 * Wrapper für RequireAuth + Suspense + ErrorBoundary (DRY für alle Admin-Routes).
 *
 * ErrorBoundary fängt Render-Errors ab statt die ganze App weiß zu lassen.
 * So bleibt das Backoffice nutzbar wenn eine Single-Page crasht.
 */
function AdminRoute({
  children,
  minRole,
  requireServiceAdmin,
  allowChatbot,
  allowApeMitarbeiter,
}: {
  children: React.ReactNode;
  minRole?: StaffRole;
  requireServiceAdmin?: boolean;
  /** Rolle 'chatbot' (Bot-Betreiber, 0067) darf NUR Routen mit diesem Flag. */
  allowChatbot?: boolean;
  /** Rolle 'ape_mitarbeiter' (Ape-Aushilfe, Runde 5) darf NUR Routen mit diesem Flag. */
  allowApeMitarbeiter?: boolean;
}) {
  return (
    <RequireAuth minRole={minRole} requireServiceAdmin={requireServiceAdmin} allowChatbot={allowChatbot} allowApeMitarbeiter={allowApeMitarbeiter}>
      <ErrorBoundary label="Admin Backoffice">
        <Suspense fallback={<AdminLoader />}>{children}</Suspense>
      </ErrorBoundary>
    </RequireAuth>
  );
}

/**
 * Routes der Sizilianer-Website.
 *
 * Phase 2A — public statische Seiten + Kontaktformular.
 * Phase 2B — Speisekarte.
 * Phase 2C.1 — Reservation Flow live.
 * Phase 2C.2 — Events (Ape-Buchung, Mobile Steinofen-Pizza) live.
 * Phase 2C.3 — Galerie live · Blog pending.
 */
export function App() {
  return (
    <ErrorBoundary label="App Root">
      <AuthProvider>
      <SaveStatusProvider>
      <NativePushNavigation />
      <Routes>
      {/* Public — Home (eager) + Legal; PublicRoute wegen Kanal-Guard Pflicht */}
      <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="/impressum" element={<PublicRoute><Impressum /></PublicRoute>} />
      <Route path="/datenschutz" element={<PublicRoute><Datenschutz /></PublicRoute>} />
      <Route path="/agb" element={<PublicRoute><AGB /></PublicRoute>} />

      {/* Public — Lazy via Suspense */}
      <Route path="/ueber-uns" element={<PublicRoute><UeberUns /></PublicRoute>} />
      <Route path="/kontakt" element={<PublicRoute><Kontakt /></PublicRoute>} />
      <Route path="/speisekarte" element={<PublicRoute><Speisekarte /></PublicRoute>} />

      {/* Public — Reservation Flow */}
      <Route path="/reservieren" element={<PublicRoute><Reservieren /></PublicRoute>} />
      <Route path="/reservation/confirmed" element={<PublicRoute><ReservationConfirmed /></PublicRoute>} />
      <Route path="/reservation/cancel" element={<PublicRoute><ReservationCancel /></PublicRoute>} />
      <Route path="/reservation/manage" element={<PublicRoute><ReservationManage /></PublicRoute>} />
      <Route path="/warteliste/buchung" element={<PublicRoute><WaitlistClaim /></PublicRoute>} />

      {/* Public — Events (Ape-Buchung) */}
      <Route path="/events" element={<PublicRoute><Events /></PublicRoute>} />
      <Route path="/events/anfrage-erhalten" element={<PublicRoute><ApeAnfrageErhalten /></PublicRoute>} />
      <Route path="/events/angebot" element={<PublicRoute><ApeQuoteAccept /></PublicRoute>} />
      <Route path="/events/buchung-bestaetigt" element={<PublicRoute><ApeBuchungBestaetigt /></PublicRoute>} />
      <Route path="/events/storno" element={<PublicRoute><ApeStorno /></PublicRoute>} />
      {/* :slug zuletzt — statische Segmente gewinnen in v6 ohnehin, aber die
          Reihenfolge soll die Rangfolge auch lesbar machen (0098) */}
      <Route path="/events/:slug" element={<PublicRoute><EventDetail /></PublicRoute>} />
      {/* Public — Abholtafel (0107). Ersetzt die Gast-SMS, die am 11.09.2026
          keine einzige von 52 Bestellungen genutzt hat. Beide Routen in
          <PublicRoute>, weil der App-Kanal keine öffentliche Route erreichen
          darf (tests/e2e/channel-guard.spec.ts). */}
      <Route path="/tafel" element={<PublicRoute><Tafel /></PublicRoute>} />
      <Route path="/tafel/:verkaufId" element={<PublicRoute><TafelVerkauf /></PublicRoute>} />

      {/* Public — Galerie */}
      <Route path="/galerie" element={<PublicRoute><Galerie /></PublicRoute>} />

      {/* Public — La Putia (Sub-Site für Laden in Innenstadt) */}
      <Route path="/la-putia" element={<PublicRoute><LaPutia /></PublicRoute>} />

      {/* Public — Blog pending */}
      <Route path="/blog" element={<PublicRoute><ComingSoon page="Blog" /></PublicRoute>} />

      {/* Public — Newsletter */}
      <Route path="/newsletter" element={<PublicRoute><Newsletter /></PublicRoute>} />
      <Route path="/newsletter/bestaetigt" element={<PublicRoute><NewsletterConfirmed /></PublicRoute>} />
      <Route path="/newsletter/abgemeldet" element={<PublicRoute><NewsletterUnsubscribed /></PublicRoute>} />

      {/* Admin Backoffice — lazy loaded chunks */}
      <Route
        path="/admin/login"
        element={
          <Suspense fallback={<AdminLoader />}>
            <AdminLogin />
          </Suspense>
        }
      />
      {/* Kellner-PIN-Login: öffentlich, nutzt die vom "Tag öffnen" gespeicherte Geräte-Liste */}
      <Route
        path="/admin/pin"
        element={
          <Suspense fallback={<AdminLoader />}>
            <PinLogin />
          </Suspense>
        }
      />
      {/* Kontor-Übernahme (2.6a): AUSSERHALB RequireAuth — hier entsteht die
          Sitzung erst. Ein Auth-Guard davor wäre eine verschlossene Tür,
          deren Schlüssel dahinter liegt. Der Setzer installiert sich nur
          im Kontor-WebView; Begründung in der Datei. */}
      <Route
        path="/admin/kontor-eintritt"
        element={
          <Suspense fallback={<AdminLoader />}>
            <KontorEintritt />
          </Suspense>
        }
      />
      {/* Sperrbildschirm der Leitung: AUSSERHALB RequireAuth — hier entsteht
          die Sitzung erst. Ein Auth-Guard davor waere eine verschlossene Tuer,
          deren Schluessel dahinter liegt (dieselbe Fehlerklasse wie oben).
          ⚠️ KEIN <PublicRoute>: „oeffentlich" heisst hier „ohne Sitzung
          erreichbar", nicht „Website-Flaeche". Der Kanal-Guard schickte diese
          Seite im App-Kanal in eine Weiterleitungsschleife nach /admin.
          Die fuenfte anmeldungsfreie Admin-Route — bewusst gezaehlt. */}
      <Route
        path="/admin/entsperren"
        element={
          <Suspense fallback={<AdminLoader />}>
            <GeraetEntsperren />
          </Suspense>
        }
      />
      {/* MFA-Challenge: braucht eingeloggten User aber NICHT AAL2 — sonst Loop */}
      <Route
        path="/admin/2fa"
        element={
          <RequireAuth skipMfaCheck allowChatbot allowApeMitarbeiter>
            <Suspense fallback={<AdminLoader />}>
              <AdminMfaChallenge />
            </Suspense>
          </RequireAuth>
        }
      />
      {/* Onboarding: braucht eingeloggten Admin, MUSS Onboarding-Redirect skippen */}
      <Route
        path="/admin/onboarding"
        element={
          <RequireAuth skipOnboardingCheck>
            <Suspense fallback={<AdminLoader />}>
              <AdminOnboarding />
            </Suspense>
          </RequireAuth>
        }
      />
      {/* Pflicht-Passwortwechsel (Initialpasswort, 0055): skippt den eigenen
          Guard UND Onboarding — sonst Redirect-Schleife Passwort ↔ Wizard.
          Reihenfolge im Guard: MFA → Passwort → Onboarding. */}
      <Route
        path="/admin/passwort-setzen"
        element={
          <RequireAuth skipPasswordCheck skipOnboardingCheck allowChatbot allowApeMitarbeiter>
            <Suspense fallback={<AdminLoader />}>
              <AdminPasswordSet mode="forced" />
            </Suspense>
          </RequireAuth>
        }
      />
      {/* Passwort-vergessen-Link (Recovery-Session aus der Reset-Mail). Ohne
          RequireAuth: die Seite behandelt "kein User" selbst (Link abgelaufen). */}
      <Route
        path="/admin/reset-password"
        element={
          <Suspense fallback={<AdminLoader />}>
            <AdminPasswordSet mode="recovery" />
          </Suspense>
        }
      />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      {/* Eigene Zugangssachen. minRole="manager", weil /admin/einstellungen
          auf "admin" steht und Manager dort nie hinkaemen — sie sind aber
          die Hauptzielgruppe der Geraete-Anmeldung. */}
      <Route path="/admin/mein-konto" element={<AdminRoute minRole="manager"><MeinKonto /></AdminRoute>} />

      {/* GÄSTE (staff) */}
      <Route path="/admin/reservierungen" element={<AdminRoute><AdminReservations /></AdminRoute>} />
      <Route path="/admin/reservierungen/kalender" element={<AdminRoute><AdminReservationCalendar /></AdminRoute>} />
      <Route path="/admin/tische" element={<AdminRoute><AdminTischuebersicht /></AdminRoute>} />
      <Route path="/admin/warteliste" element={<AdminRoute><AdminWaitlist /></AdminRoute>} />
      <Route path="/admin/qr-codes" element={<AdminRoute><AdminQrCodes /></AdminRoute>} />
      {/* Kompatibilitäts-Shim: AdminFloorPlan.tsx ist gelöscht, die alte URL kann
          aber noch in Lesezeichen stehen. Nicht entfernen. */}
      <Route path="/admin/floor-plan" element={<Navigate to="/admin/tische" replace />} />

      {/* GÄSTE/SPEISEKARTE (manager, Deckungsbeitrag admin) */}
      <Route path="/admin/events" element={<AdminRoute minRole="manager"><AdminEventsList /></AdminRoute>} />
      <Route path="/admin/events/calendar" element={<AdminRoute minRole="manager"><AdminEventCalendar /></AdminRoute>} />
      <Route path="/admin/events/new" element={<AdminRoute minRole="manager"><AdminEventNew /></AdminRoute>} />
      <Route path="/admin/events/:id" element={<AdminRoute minRole="manager"><AdminEventDetail /></AdminRoute>} />

      {/* APE-VERKAUF (Plan docs/plans/ape-verkauf.md).
          Die drei Bedienflächen stehen BEWUSST ohne minRole: E-21 verlangt,
          dass die zugewiesene Servicekraft ihren offenen Verkauf bedient.
          Die eigentliche Schranke sind die Zugriffsregeln aus Migration 0101
          (Zuweisung wird gegen auth.uid() geprüft) — eine Route-Regel ist
          kein Sicherheitstor. Der BERICHT ist dagegen ab Manager (E-20).
          Runde 5 (E-47): Die drei Flächen tragen `allowApeMitarbeiter` — sie
          sind alles, was die Ape-Aushilfe sieht. Der Bericht bewusst nicht. */}
      <Route path="/admin/ape-verkauf" element={<AdminRoute allowApeMitarbeiter><ApeVerkaufListe /></AdminRoute>} />
      <Route path="/admin/ape-verkauf/:id/annahme" element={<AdminRoute allowApeMitarbeiter><ApeVerkaufStationA /></AdminRoute>} />
      <Route path="/admin/ape-verkauf/:id/station" element={<AdminRoute allowApeMitarbeiter><ApeVerkaufStationB /></AdminRoute>} />
      <Route path="/admin/ape-verkauf/:id/bericht" element={<AdminRoute minRole="manager"><ApeVerkaufBericht /></AdminRoute>} />
      {/* Ape-Katalog und Preisrechner sind am 26.08.2026 aus dem
          Einstellungs-Reiter „Ape-Catering" hierher gezogen. Der Katalog
          wird dabei ab MANAGER erreichbar — 0101 erlaubt ihm das Schreiben
          längst, nur die Einstellungen-Route war enger. Der Rechner bleibt
          admin, weil er restaurant_settings schreibt. */}
      <Route path="/admin/ape-katalog" element={<AdminRoute minRole="manager"><ApeKatalog /></AdminRoute>} />
      <Route path="/admin/ape-rechner" element={<AdminRoute minRole="admin"><ApeRechner /></AdminRoute>} />
      <Route path="/admin/ape-verwaltung" element={<AdminRoute minRole="manager"><ApeVerwaltung /></AdminRoute>} />
      <Route path="/admin/speisekarte" element={<AdminRoute minRole="manager"><AdminMenu /></AdminRoute>} />
      <Route path="/admin/speisekarte/margen" element={<AdminRoute minRole="admin"><AdminMargins /></AdminRoute>} />
      <Route path="/admin/warenstamm" element={<AdminRoute minRole="manager"><AdminInventory /></AdminRoute>} />
      <Route path="/admin/shop" element={<AdminRoute minRole="manager"><AdminShop /></AdminRoute>} />

      {/* TEAM (manager) */}
      <Route path="/admin/schichtplan" element={<AdminRoute minRole="manager"><AdminShifts /></AdminRoute>} />
      <Route path="/admin/personal" element={<AdminRoute minRole="manager"><AdminPersonal /></AdminRoute>} />
      <Route path="/admin/kontaktanfragen" element={<AdminRoute minRole="manager"><AdminContactMessages /></AdminRoute>} />

      {/* ANALYSE (manager) */}
      <Route path="/admin/insights" element={<AdminRoute minRole="manager"><AdminInsights /></AdminRoute>} />
      <Route path="/admin/chatbot" element={<AdminRoute minRole="manager" allowChatbot><AdminChatbot /></AdminRoute>} />
      <Route path="/admin/seo" element={<AdminRoute minRole="admin"><AdminSeo /></AdminRoute>} />
      {/* Kurzform die-sizilianer.de/seo — die Seite selbst liegt unter /admin, damit sie robots.txt-Sperre, Audit-Konventionen und die iPad-App erbt. */}
      <Route path="/seo" element={<Navigate to="/admin/seo" replace />} />

      {/* SYSTEM (admin; Protokoll ab manager — RLS staffelt die Sicht) */}
      <Route path="/admin/mail" element={<AdminRoute minRole="admin"><AdminMail /></AdminRoute>} />
      <Route path="/admin/newsletter" element={<AdminRoute minRole="admin"><AdminNewsletterCampaigns /></AdminRoute>} />
      <Route path="/admin/newsletter/abonnenten" element={<AdminRoute minRole="admin"><AdminNewsletterSubscribers /></AdminRoute>} />
      <Route path="/admin/newsletter/rabattcodes" element={<AdminRoute minRole="admin"><AdminDiscountCodes /></AdminRoute>} />
      <Route path="/admin/protokoll" element={<AdminRoute minRole="manager"><AdminProtokoll /></AdminRoute>} />
      <Route path="/admin/einstellungen" element={<AdminRoute minRole="admin"><AdminSettings /></AdminRoute>} />

      {/* SUPER-ADMIN (is_service_admin) */}
      <Route path="/admin/service" element={<AdminRoute requireServiceAdmin><AdminService /></AdminRoute>} />
      <Route path="/admin/cron-status" element={<AdminRoute requireServiceAdmin><AdminCronStatus /></AdminRoute>} />
      <Route path="/admin/cron-probe" element={<AdminRoute requireServiceAdmin><AdminCronProbe /></AdminRoute>} />

      {/* Internal — nur in Dev verfügbar (FIND-013 Production-Hardening) */}
      {import.meta.env.DEV && (
        <Route path="/dev/design-system" element={<PublicRoute><DesignSystemShowcase /></PublicRoute>} />
      )}
      {import.meta.env.DEV && (
        <Route path="/dev/symbole" element={<PublicRoute><SymbolGallery /></PublicRoute>} />

      )}

      {/* Catch-all — im App-Kanal führt der Guard unbekannte URLs (auch
          /admin-Tippfehler aus alten Push-Deep-Links) aufs Dashboard statt
          auf die Public-404 */}
      <Route path="*" element={<PublicRoute><NotFound /></PublicRoute>} />
      </Routes>
      </SaveStatusProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function ComingSoon({ page }: { page: string }) {
  return (
    <PublicPageTemplate pageTitle={page}>
      <section className="mx-auto max-w-2xl px-6 py-24 flex flex-col items-center text-center gap-4">
        <span className="font-script text-2xl text-ink-soft">A presto</span>
        <h1 className="font-serif text-4xl md:text-5xl text-ink-2">{page}</h1>
        <Tricolore variant="compact" />
        <p className="font-sans text-base text-ink-soft mt-2 max-w-md">
          Diese Seite ist gerade im Aufbau und erscheint demnächst. Bis dahin
          erreichen Sie uns gerne telefonisch oder per Mail.
        </p>
        <div className="flex gap-3 mt-4">
          <ButtonLink to="/" variant="primary">Zur Startseite</ButtonLink>
          <ButtonLink to="/kontakt" variant="secondary">Kontakt aufnehmen</ButtonLink>
        </div>
      </section>
    </PublicPageTemplate>
  );
}
